import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const STAGES = {
  pending_manager: { label: "Pending Manager", color: "bg-amber-50 text-amber-800 border-amber-200" },
  pending_employee: { label: "Pending Employee", color: "bg-blue-50 text-blue-800 border-blue-200" },
  pending_final: { label: "Pending Final", color: "bg-violet-50 text-violet-800 border-violet-200" },
  completed: { label: "Completed", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

const QUARTERS = ["All", "Q1", "Q2", "Q3", "Q4"];

export default function Reviews() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [bus, setBus] = useState([]);
  const [activeBu, setActiveBu] = useState(() => params.get("bu") || "all");
  const [activeQ, setActiveQ] = useState(() => {
    const q = params.get("quarter");
    return QUARTERS.includes(q) ? q : "All";
  });

  useEffect(() => { (async () => {
    const [r, u, b] = await Promise.all([
      api.get("/reviews"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/business-units").catch(() => ({ data: [] })),
    ]);
    setReviews(r.data); setUsers(u.data); setBus(b.data);
  })(); }, []);

  const uname = (id) => users.find((u) => u.id === id)?.name || id;
  const buById = useMemo(() => Object.fromEntries(bus.map((b) => [b.id, b])), [bus]);

  const filtered = useMemo(() => {
    return reviews.filter((r) => {
      if (activeBu !== "all" && r.template_snapshot?.business_unit_id !== activeBu) return false;
      if (activeQ !== "All" && r.quarter !== activeQ) return false;
      return true;
    });
  }, [reviews, activeBu, activeQ]);

  return (
    <div>
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Reviews</div>
        <h1 className="text-4xl font-bold text-zinc-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          KPA reviews
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {user?.role === "user" ? "Your reviews and self-assessments." :
           user?.role === "bu_manager" ? "Reviews you own and your own employee reviews." :
           user?.role === "bu_owner" ? "All reviews in your Business Unit." :
           "All reviews across the organisation."}
        </p>
      </div>

      {/* BU tabs */}
      {bus.length > 1 && (
        <div className="border-b border-zinc-200 mb-4 flex gap-6 overflow-x-auto">
          <TabBtn label="All Business Units" active={activeBu === "all"} onClick={() => setActiveBu("all")} testId="reviews-tab-all" />
          {bus.map((b) => (
            <TabBtn key={b.id} label={b.name} logo={b.logo} active={activeBu === b.id} onClick={() => setActiveBu(b.id)} testId={`reviews-tab-${b.id}`} />
          ))}
        </div>
      )}

      {/* Quarter filter */}
      <div className="flex gap-2 mb-6">
        {QUARTERS.map((q) => (
          <button key={q} onClick={() => setActiveQ(q)} data-testid={`reviews-q-${q}`}
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${
              activeQ === q ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            }`}>{q}</button>
        ))}
      </div>

      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-5 py-3">Template</th>
              <th className="text-left px-5 py-3">Employee</th>
              <th className="text-left px-5 py-3">Manager</th>
              <th className="text-left px-5 py-3">Quarter / FY</th>
              <th className="text-left px-5 py-3">Stage</th>
              <th className="text-right px-5 py-3">Final</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const s = STAGES[r.stage] || { label: r.stage, color: "" };
              const bu = buById[r.template_snapshot?.business_unit_id];
              return (
                <tr key={r.id} className="border-t border-zinc-200 hover:bg-zinc-50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {bu?.logo && (
                        <img src={bu.logo} alt="" className="h-7 w-auto max-w-[64px] object-contain flex-shrink-0" />
                      )}
                      <Link to={`/reviews/${r.id}`} className="font-medium hover:underline" data-testid={`review-row-${r.id}`}>{r.template_snapshot?.name}</Link>
                    </div>
                  </td>
                  <td className="px-5 py-3">{uname(r.employee_id)}</td>
                  <td className="px-5 py-3">{uname(r.manager_id)}</td>
                  <td className="px-5 py-3 text-zinc-600">{r.quarter} · {r.fiscal_year}</td>
                  <td className="px-5 py-3"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 border rounded-sm ${s.color}`}>{s.label}</span></td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold">{r.final_total ?? "—"}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-16 text-center text-zinc-500">
                No reviews match the filter.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TabBtn({ label, logo, active, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`pb-3 text-sm border-b-2 -mb-px whitespace-nowrap transition-colors flex items-center gap-2 ${
        active ? "border-zinc-900 text-zinc-900 font-semibold" : "border-transparent text-zinc-500 hover:text-zinc-900"
      }`}>
      {logo && <img src={logo} alt="" className="h-5 w-auto max-w-[40px] object-contain" />}
      {label}
    </button>
  );
}
